import { Fragment, useCallback, useState } from 'react'
import { isEmpty, omit } from 'lodash'
import { Formik, Field, Form, FormikErrors } from 'formik'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import Modal from 'components/Modal'
import TextBox from 'components/TextBox'
import SelectDropdown from 'components/SelectDropdown'
import Loader from 'components/Loader'
import ToggleButton from 'components/ToggleButton'

import { useGetAccountsQuery, useUpdateAccountMutation } from 'store/porticoApi/accounts'
import { useGetRolesQuery } from 'store/porticoApi/roles'
import {
  useCreateRoleAssigmentMutation,
  useUpdateRoleAssignmentMutation,
} from 'store/porticoApi/roleAssignments'
import { useGetVenuesQuery } from 'store/porticoApi/venues'
import { TRole } from 'types/roles'
import { TEditAccountFormValues, TRoleAssignmentWithAssociatedRole } from 'types/accounts'
import { transformRoleAssignmentsToDefaultOptionRoles } from 'utils/accounts/transformRoleAssignmentsToDefaultOptionRoles'
import { filterRolesByAppTypeAndName } from 'utils/accounts/filterRolesByAppTypeAndName'
import { setEditAccountRoleAssignments } from 'utils/accounts/setEditAccountRoleAssignments'

const EditAccount = () => {
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const { id } = useParams()
  const navigate = useNavigate()

  const { data: venues, isLoading: isLoadingVenues } = useGetVenuesQuery()
  const { data: roles, isLoading: isLoadingRoles } = useGetRolesQuery()
  const { currentAccount, isLoading: isLoadingCurrentAccount } = useGetAccountsQuery(undefined, {
    selectFromResult: ({ data, isLoading }) => ({
      currentAccount: data?.filter((account) => account.id === id)[0],
      isLoading,
    }),
  })

  const [createRoleAssigment] = useCreateRoleAssigmentMutation()
  const [updateAccount] = useUpdateAccountMutation()
  const [updateRoleAssignment] = useUpdateRoleAssignmentMutation()

  const { firstName, lastName, email, phoneNumber, roleAssignments, accountStatus } =
    currentAccount ?? {}

  const isLoading = isLoadingVenues || isLoadingRoles || isLoadingCurrentAccount
  const isInvitationPendingStatus = accountStatus === 'invitation pending'

  const initialValues: TEditAccountFormValues = {
    firstName: firstName ?? '',
    lastName: lastName ?? '',
    email: email ?? '',
    phoneNumber: phoneNumber ?? '',
    roleAssignments: roleAssignments ?? [],
    deactivateAccount: accountStatus === 'unconfirmed' ? true : false,
  }

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required('Required'),
    lastName: Yup.string().required('Required'),
    email: Yup.string().required('Required'),
  })

  const disableUpdateButton = useCallback(
    (dirty: boolean, errors: FormikErrors<TEditAccountFormValues>) => {
      const disableButton = !dirty || (showValidationErrors && !isEmpty(errors))

      return disableButton
    },
    [showValidationErrors]
  )

  const onClickSaveButton = useCallback(() => {
    setShowValidationErrors(true)
  }, [])

  const transformRolesAsSelectOptions = filterRolesByAppTypeAndName(roles as TRole[])?.map(
    ({ name, uuid }) => ({
      name,
      uuid,
    })
  )

  const onCloseModal = () => navigate('/accounts')

  const onSubmit = (values: TEditAccountFormValues) => {
    const { firstName, lastName, email, phoneNumber, roleAssignments, deactivateAccount } = values

    const deactivateAccountApiValue = deactivateAccount ? 'unconfirmed' : 'active'

    const updateAccountValues = {
      firstName,
      lastName,
      email,
      phoneNumber,
      accountStatus:
        accountStatus === 'invitation pending' ? 'invitation pending' : deactivateAccountApiValue,
      id: currentAccount?.id as string,
    }

    // role assignments to update have initial structure of roleAssignments, that's why I check if user
    // do not create new role assignment, it means if roleAssignment has property userId
    const roleAssignmentsToUpdate = roleAssignments
      ?.filter((roleAssignment) => roleAssignment.userId !== undefined)
      ?.map((roleAssignment) => omit(roleAssignment, 'userRoles'))

    // new role assignments have structure: {userRoleId, venueId}
    const roleAssignmentsToCreate = roleAssignments?.filter(
      (roleAssignment) => roleAssignment.userId === undefined
    )

    const transformRoleAssignments = (userId: string) =>
      roleAssignmentsToCreate?.map(
        ({ userRoleId, venueId }): { userRoleId: string; venueId: string; userId: string } => ({
          userRoleId,
          venueId,
          userId,
        })
      )

    updateAccount(updateAccountValues)
      .unwrap()
      .then(() => {
        navigate('/accounts')
        toast.success('Success!', {
          position: toast.POSITION.BOTTOM_CENTER,
        })
      })
    updateRoleAssignment(roleAssignmentsToUpdate)
      .unwrap()
      .then(() => {
        navigate('/accounts')
        toast.success('Success!', {
          position: toast.POSITION.BOTTOM_CENTER,
        })
      })

    // by roleAssignmentsToCreate we can check if we need to create new role assignment in
    // edit account form
    if (!isEmpty(roleAssignmentsToCreate)) {
      createRoleAssigment(transformRoleAssignments(currentAccount?.id as string))
        .unwrap()
        .then(() => {
          navigate('/accounts')
          toast.success('Success!', {
            position: toast.POSITION.BOTTOM_CENTER,
          })
        })
    }
  }

  if (isLoading) {
    return <Loader />
  }

  return (
    <Modal title="Edit Account" onClose={onCloseModal} className="-narrow">
      <div className="modal-body">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ dirty, errors, setFieldValue, values }) => (
            <Form className="item-settings">
              <section className="item-settings__section ">
                <div className="content-wrapper">
                  <Field
                    component={TextBox}
                    label="First Name"
                    name="firstName"
                    wrapperClassName="-space-right"
                    showValidationErrors={showValidationErrors}
                    onChange={(value: string) => setFieldValue('firstName', value)}
                    value={values?.firstName}
                    error={errors?.firstName}
                  />
                  <Field
                    component={TextBox}
                    label="Last Name"
                    name="lastName"
                    showValidationErrors={showValidationErrors}
                    onChange={(value: string) => setFieldValue('lastName', value)}
                    value={values?.lastName}
                    error={errors?.lastName}
                  />
                </div>
                <div className="text-field">
                  <Field
                    component={TextBox}
                    label="Email Address"
                    name="email"
                    showValidationErrors={showValidationErrors}
                    onChange={(value: string) => setFieldValue('email', value)}
                    value={values?.email}
                    error={errors?.email}
                  />
                </div>
                <div className="text-field">
                  <Field
                    component={TextBox}
                    label="Phone Number"
                    name="phoneNumber"
                    onChange={(value: string) => setFieldValue('phoneNumber', value)}
                    value={values?.phoneNumber}
                    error={errors?.phoneNumber}
                  />
                </div>
                <div className="item-settings__border-break"></div>
                <h3 className="header">Roles</h3>
                <div className="row">
                  {venues?.map(({ venueUuid, venueName }) => (
                    <Fragment key={venueUuid}>
                      <div className="content-wrapper">
                        <span className="content-header">{venueName}</span>
                        <Field
                          component={SelectDropdown}
                          options={transformRolesAsSelectOptions}
                          defaultOption={transformRoleAssignmentsToDefaultOptionRoles(
                            roleAssignments as TRoleAssignmentWithAssociatedRole[],
                            venueUuid
                          )}
                          onChange={(value: { name: string; uuid: string }) =>
                            setEditAccountRoleAssignments(value, values, venueUuid, setFieldValue)
                          }
                          shorter
                        />
                      </div>
                      <div className="item-settings__border-break"></div>
                    </Fragment>
                  ))}
                </div>
                {!isInvitationPendingStatus && (
                  <>
                    <div className="content-wrapper pt-mxs pb-0">
                      <div className="content-header">Deactivate this account</div>
                      <Field
                        component={ToggleButton}
                        name="deactivateAccount"
                        onChange={(value: boolean) => setFieldValue('deactivateAccount', value)}
                      />
                    </div>
                    <span className="content-short-info">
                      Accounts may not be deleted for reporting and audits.
                    </span>
                  </>
                )}
                {isInvitationPendingStatus && (
                  <div className="row">
                    <div className="content-wrapper pt-mxs">
                      <span className="content-header">Account Status</span>
                      <div className="content-wrapper pb-0">
                        <span className="items-dot -warning"></span>
                        <span className="content-header">Invitation Pending</span>
                      </div>
                    </div>
                  </div>
                )}
              </section>
              <div className="btn-wrapper">
                <button
                  className="btn -update"
                  onClick={onClickSaveButton}
                  disabled={disableUpdateButton(dirty, errors)}
                  type="submit"
                >
                  Save
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}

export default EditAccount
